import React from 'react';
import {
  Card,
  Tabs,
  Descriptions,
  Tag,
  Table,
  Timeline,
  Button,
  Space,
  Typography,
  Avatar,
  Spin,
} from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  TeamOutlined,
  EditOutlined,
  KeyOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import type { UserDetailProps, LoginLog, OperationLog } from '../types';
import { DesignTokens, getUserStatusColor, getLoginStatusColor } from '../DesignTokens';
import { maskPhone, formatDateTime } from '../utils';

const { Title, Text } = Typography;

export const UserDetail: React.FC<UserDetailProps> = ({
  userId,
  data,
  loading = false,
  onEdit,
  onResetPassword,
}) => {
  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 400,
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!data) {
    return (
      <div style={{ padding: DesignTokens.spacing.xl }}>
        <Card>
          <Text type="secondary">用户数据不存在</Text>
        </Card>
      </div>
    );
  }

  const { user, permissions, loginLogs, operationLogs } = data;

  // 登录日志表格列定义
  const loginLogColumns: ColumnsType<LoginLog> = [
    {
      title: '登录时间',
      dataIndex: 'loginTime',
      key: 'loginTime',
      width: 180,
      render: (time: string) => formatDateTime(time),
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 140,
    },
    {
      title: '设备信息',
      dataIndex: 'device',
      key: 'device',
    },
    {
      title: '位置',
      dataIndex: 'location',
      key: 'location',
      width: 140,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      align: 'center',
      render: (status: LoginLog['status']) => (
        <Tag
          icon={
            status === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />
          }
          color={status === 'success' ? 'success' : 'error'}
        >
          {status === 'success' ? '成功' : '失败'}
        </Tag>
      ),
    },
  ];

  // Tab内容
  const tabItems = [
    {
      key: 'basic',
      label: '基本信息',
      children: (
        <Card
          title="用户信息"
          extra={
            <Space>
              <Button icon={<EditOutlined />} onClick={onEdit}>
                编辑
              </Button>
              <Button icon={<KeyOutlined />} onClick={onResetPassword}>
                重置密码
              </Button>
            </Space>
          }
          style={{ marginBottom: DesignTokens.spacing.lg }}
        >
          <div style={{ display: 'flex', gap: DesignTokens.spacing.xl }}>
            {/* 头像区域 */}
            <div style={{ textAlign: 'center' }}>
              <Avatar
                size={120}
                icon={<UserOutlined />}
                style={{
                  backgroundColor: DesignTokens.colors.primary,
                  marginBottom: DesignTokens.spacing.md,
                }}
              />
              <div>
                <Title level={4} style={{ margin: 0 }}>
                  {user.realName}
                </Title>
                <Text type="secondary">@{user.username}</Text>
              </div>
            </div>

            {/* 详细信息 */}
            <div style={{ flex: 1 }}>
              <Descriptions column={2} bordered>
                <Descriptions.Item label="用户ID">{user.id}</Descriptions.Item>
                <Descriptions.Item label="用户名">{user.username}</Descriptions.Item>
                <Descriptions.Item
                  label={
                    <span>
                      <PhoneOutlined style={{ marginRight: 4 }} />
                      手机号
                    </span>
                  }
                >
                  {maskPhone(user.phone)}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <span>
                      <MailOutlined style={{ marginRight: 4 }} />
                      邮箱
                    </span>
                  }
                >
                  {user.email || '-'}
                </Descriptions.Item>
                <Descriptions.Item
                  label={
                    <span>
                      <TeamOutlined style={{ marginRight: 4 }} />
                      部门
                    </span>
                  }
                >
                  {user.department || '-'}
                </Descriptions.Item>
                <Descriptions.Item label="状态">
                  <Tag
                    color={user.status === 'active' ? 'success' : 'default'}
                    icon={
                      <span
                        style={{
                          display: 'inline-block',
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          backgroundColor: getUserStatusColor(user.status),
                          marginRight: 4,
                        }}
                      />
                    }
                  >
                    {user.status === 'active' ? '正常' : '禁用'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="创建时间">
                  {dayjs(user.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                </Descriptions.Item>
                <Descriptions.Item label="最后登录">
                  {user.lastLogin
                    ? dayjs(user.lastLogin).format('YYYY-MM-DD HH:mm:ss')
                    : '-'}
                </Descriptions.Item>
              </Descriptions>
            </div>
          </div>
        </Card>
      ),
    },
    {
      key: 'roles',
      label: '角色权限',
      children: (
        <Card title="角色列表">
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {user.roles.map((role) => (
              <Card
                key={role.id}
                size="small"
                style={{ backgroundColor: DesignTokens.colors.gray2 }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <Title level={5} style={{ margin: 0 }}>
                      {role.name}
                    </Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      代码: {role.code}
                    </Text>
                  </div>
                  <Tag color="blue">当前角色</Tag>
                </div>
              </Card>
            ))}
          </Space>

          {permissions.length > 0 && (
            <div style={{ marginTop: DesignTokens.spacing.xl }}>
              <Descriptions title="权限详情" column={1} bordered>
                {permissions.map((perm) => (
                  <Descriptions.Item key={perm.id} label={perm.name}>
                    <Space wrap>
                      {perm.children?.map((child) => (
                        <Tag key={child.id} color="geekblue">
                          {child.name}
                        </Tag>
                      ))}
                    </Space>
                  </Descriptions.Item>
                ))}
              </Descriptions>
            </div>
          )}
        </Card>
      ),
    },
    {
      key: 'loginLogs',
      label: '登录日志',
      children: (
        <Card>
          <Table
            columns={loginLogColumns}
            dataSource={loginLogs}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </Card>
      ),
    },
    {
      key: 'operationLogs',
      label: '操作记录',
      children: (
        <Card>
          <Timeline
            items={operationLogs.map((log) => ({
              key: log.id,
              color:
                log.result === 'success'
                  ? DesignTokens.colors.success
                  : DesignTokens.colors.error,
              children: (
                <div>
                  <div style={{ ...DesignTokens.typography.bodyRegular }}>
                    <strong>{log.action}</strong>
                    <Tag
                      color={log.result === 'success' ? 'success' : 'error'}
                      style={{ marginLeft: 8 }}
                    >
                      {log.result === 'success' ? '成功' : '失败'}
                    </Tag>
                  </div>
                  <div
                    style={{
                      ...DesignTokens.typography.bodySmall,
                      color: DesignTokens.colors.gray7,
                      marginTop: 4,
                    }}
                  >
                    目标: {log.target}
                  </div>
                  <div
                    style={{
                      ...DesignTokens.typography.bodySmall,
                      color: DesignTokens.colors.gray6,
                      marginTop: 2,
                    }}
                  >
                    操作人: {log.operator} | 时间:{' '}
                    {dayjs(log.time).format('YYYY-MM-DD HH:mm:ss')}
                  </div>
                </div>
              ),
            }))}
          />
        </Card>
      ),
    },
  ];

  return (
    <div style={{ padding: DesignTokens.spacing.xl }}>
      {/* 顶部标题 */}
      <div style={{ marginBottom: DesignTokens.spacing.xl }}>
        <Title level={2} style={{ margin: 0, ...DesignTokens.typography.h2 }}>
          用户详情
        </Title>
        <div
          style={{
            ...DesignTokens.typography.caption,
            color: DesignTokens.colors.gray7,
            marginTop: 4,
          }}
        >
          系统管理 / 用户管理 / 用户详情
        </div>
      </div>

      {/* Tab内容 */}
      <Card>
        <Tabs items={tabItems} defaultActiveKey="basic" />
      </Card>
    </div>
  );
};
